import {
  createContext,
  useContext,
  useEffect,
  useReducer,
  useRef,
  useCallback,
  useState,
  useMemo,
} from "react";
import { useFetchFilterCoin } from "../useReactQuery";
import { useSelector } from "react-redux";
import { useUsdtPrice } from "./useUsdtPrice";
import webSocketService from "../../../services/WebSocketService";
import { useWebSocketState } from "../../../hooks/useWebSocketState";
import { logWebSocket, logInfo } from "../../../utils/logger";

const USDT_PRICE_API_TIMEOUT = 5000;
const RECONNECT_INTERVAL = 15000;

// Determine preferred server from environment
const preferredServer = process.env.REACT_APP_SOCKET_TYPE === "germany" ? "germany" : "iran";

const initialState = {
  prices: JSON.parse(localStorage.getItem("coins") || "[]") || [],
  usdtPrice: JSON.parse(sessionStorage.getItem("usdtPrice") || "null") || null,
};

const reducer = (state, action) => {
  switch (action.type) {
    case "SET_PRICES":
      console.log('🔍 Reducer SET_PRICES:', {
        payloadLength: action.payload?.length || 0,
        payloadSample: action.payload?.slice(0, 3)?.map(p => ({ symbol: p.symbol, price: p.price }))
      });
      return { ...state, prices: action.payload };
    case "SET_FILTERED_PRICES":
      console.log('🔍 Reducer SET_FILTERED_PRICES:', {
        payloadLength: action.payload?.length || 0,
        payloadSample: action.payload?.slice(0, 3)?.map(p => ({ symbol: p.symbol, price: p.price }))
      });

      // Add stack trace for empty arrays to debug the issue
      if (action.payload?.length === 0) {
        console.log('❌ Empty array being set! Stack trace:');
        console.trace();
      }

      return { ...state, prices: action.payload };
    case "SET_USDT_PRICE":
      logWebSocket('Reducer: Setting USDT price', { price: action.payload?.USDTIRT });
      return { ...state, usdtPrice: action.payload };
    default:
      return state;
  }
};

const CoinPriceContext = createContext({ state: initialState });

export const CoinPriceProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const lastMessageTime = useRef(Date.now());
  const { data: filterCoins, isLoading: filterLoading, error: filterError } = useFetchFilterCoin();

  // Track filter data changes
  useEffect(() => {
    console.log('🔍 Filter data changed in CoinPriceProvider:', {
      filterCoinsLength: filterCoins?.length || 0,
      filterLoading,
      filterError: !!filterError,
      timestamp: new Date().toISOString()
    });
  }, [filterCoins, filterLoading, filterError]);
  const isLogged = useSelector((state) => state?.auth)?.userData?.id;
  const [timeoutReached, setTimeoutReached] = useState(false);

  // Legacy compatibility refs
  const socket = useRef(null);
  const connectionState = useRef({ connected: false });
  const usdtPriceFromAPI = useUsdtPrice();

  // Debug filter data
  useEffect(() => {
    if (filterCoins && filterCoins.length > 0) {
      logInfo(`useCoinPrice - Filter data loaded: ${filterCoins.length} active coins`);
    } else if (filterError) {
      console.error('useCoinPrice - Filter data error:', filterError);
    }
  }, [filterCoins, filterError]);

  // Ref to track last filtering operation to prevent duplicate logs
  const lastFilteringRef = useRef({
    inputLength: 0,
    outputLength: 0,
    timestamp: 0,
    markets: []
  });

  // Function to filter prices based on supported markets with deduplication
  const filterSupportedMarkets = useCallback((prices) => {
    console.log('🔍 filterSupportedMarkets called:', {
      pricesLength: prices?.length || 0,
      filterCoinsLength: filterCoins?.length || 0,
      filterLoading,
      timestamp: new Date().toISOString()
    });

    // Ensure filterLoading is always a boolean to prevent undefined issues
    const isFilterLoading = Boolean(filterLoading);

    // Check if filter data is still loading
    if (isFilterLoading) {
      // While filter data is loading, return the original prices to prevent showing empty state
      // This prevents the race condition where filtering returns empty arrays
      console.log('⚠️ Filter data still loading, returning original prices to prevent empty state');
      return prices;
    }

    // Check if filter data failed to load or is empty
    if (!filterCoins || !Array.isArray(filterCoins) || filterCoins.length === 0) {
      // Try to get cached filter data from localStorage as fallback
      const cachedFilterData = localStorage.getItem('cached_filter_coins');
      let fallbackFilterCoins = null;

      if (cachedFilterData) {
        try {
          fallbackFilterCoins = JSON.parse(cachedFilterData);
          if (Date.now() - lastFilteringRef.current.timestamp > 30000) {
            logInfo('Using cached filter data as fallback');
            lastFilteringRef.current.timestamp = Date.now();
          }
        } catch (error) {
          console.warn('Failed to parse cached filter data:', error);
        }
      }

      // If no cached data available either, return original prices to prevent empty state
      if (!fallbackFilterCoins || !Array.isArray(fallbackFilterCoins) || fallbackFilterCoins.length === 0) {
        if (Date.now() - lastFilteringRef.current.timestamp > 30000) {
          logInfo('No filter data available and no cached fallback, returning original prices to prevent empty state');
          lastFilteringRef.current.timestamp = Date.now();
        }
        console.log('⚠️ No filter data available, returning original prices to prevent empty state');
        return prices;
      }

      // Use fallback data
      filterCoins = fallbackFilterCoins;
    } else {
      // Cache the current filter data for future fallback use
      localStorage.setItem('cached_filter_coins', JSON.stringify(filterCoins));
    }

    // Create a Set of active (supported) coin symbols for faster lookup
    const activeCoinSymbols = new Set(
      filterCoins
        .filter(coin => coin.status === 1) // Only active coins (status = 1)
        .map(coin => coin.symbol?.toUpperCase())
    );

    // Filter prices to only include supported markets
    const filteredPrices = prices.filter(price => {
      // Handle different data structures:
      // Germany server: price.label = "ONTUSDT", price.symbol = "ONT"
      // Iran server: price.symbol = "ONTUSDT"
      const tradingPair = price.label?.toUpperCase() || price.symbol?.toUpperCase();

      if (!tradingPair) {
        return false;
      }

      // Extract base symbol from trading pair
      const baseSymbol = tradingPair?.replace('USDT', '').replace('IRT', '');

      // Include if the base symbol is in our supported list
      const isSupported = activeCoinSymbols.has(baseSymbol);

      return isSupported;
    });

    // Check if filtering results have changed to avoid duplicate logs
    const currentMarkets = filteredPrices.slice(0, 10).map(p => p.label || p.symbol);
    const hasChanged =
      lastFilteringRef.current.inputLength !== prices.length ||
      lastFilteringRef.current.outputLength !== filteredPrices.length ||
      JSON.stringify(lastFilteringRef.current.markets) !== JSON.stringify(currentMarkets);

    // Only log if results have changed or it's been more than 5 minutes
    const timeSinceLastLog = Date.now() - lastFilteringRef.current.timestamp;
    if (hasChanged || timeSinceLastLog > 300000) { // 5 minutes
      logInfo(`Filtering ${prices.length} WebSocket markets using ${Array.from(activeCoinSymbols).length} active coins from REST API`);
      logInfo(`WebSocket filtering result: ${prices.length} → ${filteredPrices.length} markets`);

      // Update last filtering ref
      lastFilteringRef.current = {
        inputLength: prices.length,
        outputLength: filteredPrices.length,
        timestamp: Date.now(),
        markets: currentMarkets
      };
    }

    // Add debug logging for empty results
    if (filteredPrices.length === 0 && prices.length > 0) {
      console.log('❌ filterSupportedMarkets returning empty array despite having input data:', {
        inputLength: prices.length,
        filterCoinsLength: filterCoins?.length || 0,
        activeCoinSymbolsLength: Array.from(activeCoinSymbols).length,
        sampleInputSymbols: prices.slice(0, 3).map(p => p.symbol || p.label),
        activeCoinSymbols: Array.from(activeCoinSymbols).slice(0, 10)
      });
      console.trace();
    }

    return filteredPrices;
  }, [filterCoins, filterLoading]);

  const resetOrderBookTimeouts = useCallback(() => {
    console.log("Resetting order book timeouts...");
  }, []);

  const resetDealsTimeouts = useCallback(() => {
    console.log("Resetting deals timeouts...");
  }, []);

  // Use unified WebSocket service instead of old enhanced socket

  // Use useSelector to listen for USDT price updates from Redux store
  const reduxMarketData = useSelector(state => state.websocket?.marketData);

  useEffect(() => {
    // Check if we have USDT price data in the Redux market data
    if (reduxMarketData?.USDTIRT) {
      lastMessageTime.current = Date.now();
      if (process.env.NODE_ENV === 'development') {
        console.log('💰 USDT price update from Redux:', reduxMarketData.USDTIRT);
      }
      dispatch({ type: "SET_USDT_PRICE", payload: { USDTIRT: reduxMarketData.USDTIRT } });
      sessionStorage.setItem("usdtPrice", JSON.stringify({ USDTIRT: reduxMarketData.USDTIRT }));
    }
  }, [reduxMarketData?.USDTIRT, dispatch]);

  // Use WebSocket state hook for better connection detection
  const { isTradingConnected, connections } = useWebSocketState();

  useEffect(() => {
    console.log('🔍 usePrice WebSocket effect triggered:', {
      isInitialized: webSocketService.isInitialized,
      isTradingConnected,
      connections
    });

    // Subscribe to trading WebSocket for price data
    if (webSocketService.isInitialized && isTradingConnected) {
      logInfo('Trading WebSocket connected, setting up subscriptions');
      setupSubscriptions();
    } else if (webSocketService.isInitialized && !isTradingConnected) {
      logWebSocket('Waiting for trading WebSocket connection...');
    }

    function setupSubscriptions() {
      logInfo('Setting up WebSocket subscriptions for price data');

      // Determine event names based on actual connected server
      const tradingState = webSocketService.connectionStates.get('trading');
      const isIranServer = tradingState?.currentServer === 'Iran';
      const marketEvent = isIranServer ? 'market' : 'prices';

      logWebSocket(`Using ${marketEvent} event for ${isIranServer ? 'Iran' : 'Germany'} server`);

      // Subscribe to market data with proper format
      const marketSubscriptionData = isIranServer ? { type: 'market' } : {};
      const unsubscribePrices = webSocketService.subscribe('trading', marketEvent, marketSubscriptionData, (data) => {
        logWebSocket(`${marketEvent} data received in usePrice`, {
          dataLength: Array.isArray(data) ? data.length : 'single item'
        });

        // Handle Iran server response format
        if (isIranServer) {
          // Parse Iran server JSON response
          let parsedData = data;
          if (typeof data === 'string') {
            try {
              parsedData = JSON.parse(data);
            } catch (error) {
              console.error('❌ Error parsing Iran market data:', error);
              return;
            }
          }

          // Iran server may send data in different formats
          let marketData = parsedData;

          // If data has a 'data' property, extract it
          if (parsedData && parsedData.data && Array.isArray(parsedData.data)) {
            marketData = parsedData.data;
          } else if (parsedData && parsedData.type === 'markets' && parsedData.data) {
            marketData = parsedData.data;
          }

          if (Array.isArray(marketData)) {
            // Process the data to extract updated prices from changes.price_usd
            const processedData = marketData.map(item => {
              // If changes.price_usd exists, use it to update the main price field
              if (item.changes && item.changes.price_usd) {
                const updatedItem = {
                  ...item,
                  price: item.changes.price_usd, // Update main price with the new price from changes
                  originalPrice: item.price // Keep original price for reference
                };

                // Debug: Log price updates for TRX
                if (item.symbol === 'TRX') {
                  console.log('🔄 TRX price update:', {
                    original: item.price,
                    updated: item.changes.price_usd,
                    symbol: item.symbol
                  });
                }

                return updatedItem;
              }
              return item;
            });

            // Store original unfiltered data for re-filtering when filter data loads
            const dataWithSource = processedData.map(item => ({ ...item, priceSource: 'unified-iran' }));
            localStorage.setItem("coins_original", JSON.stringify(dataWithSource));
            localStorage.setItem("price_source", "unified-iran");

            const filteredData = filterSupportedMarkets(processedData);
            const filteredDataWithSource = filteredData.map(item => ({ ...item, priceSource: 'unified-iran' }));

            // Only update Redux state and localStorage if we have valid filter data (not during loading)
            if (!filterLoading && filterCoins && filterCoins.length > 0) {
              dispatch({ type: "SET_FILTERED_PRICES", payload: filteredDataWithSource });
              console.log('💾 Setting localStorage coins (Iran):', filteredDataWithSource.length);
              localStorage.setItem("coins", JSON.stringify(filteredDataWithSource));
            } else {
              console.log('⚠️ Skipping Redux and localStorage update - filter data loading or unavailable');
            }
          }
        } else {
          // Germany server format
          if (Array.isArray(data)) {
            // Process the data to extract updated prices from changes.price_usd
            const processedData = data.map(item => {
              // If changes.price_usd exists, use it to update the main price field
              if (item.changes && item.changes.price_usd) {
                return {
                  ...item,
                  price: item.changes.price_usd, // Update main price with the new price from changes
                  originalPrice: item.price // Keep original price for reference
                };
              }
              return item;
            });

            // Store original unfiltered data for re-filtering when filter data loads
            const dataWithSource = processedData.map(item => ({ ...item, priceSource: 'unified-germany' }));
            localStorage.setItem("coins_original", JSON.stringify(dataWithSource));
            localStorage.setItem("price_source", "unified-germany");

            const filteredData = filterSupportedMarkets(processedData);
            const filteredDataWithSource = filteredData.map(item => ({ ...item, priceSource: 'unified-germany' }));

            // Only update Redux state and localStorage if we have valid filter data (not during loading)
            if (!filterLoading && filterCoins && filterCoins.length > 0) {
              dispatch({ type: "SET_FILTERED_PRICES", payload: filteredDataWithSource });
              console.log('💾 Setting localStorage coins (Germany):', filteredDataWithSource.length);
              localStorage.setItem("coins", JSON.stringify(filteredDataWithSource));
            } else {
              console.log('⚠️ Skipping Redux and localStorage update - filter data loading or unavailable');
            }
          }
        }
      });

      // Subscribe to USDT price updates
      let unsubscribeIndexPrice, unsubscribeUsdtPrice;

      if (isIranServer) {
        // Iran server: Subscribe with proper JSON format
        unsubscribeIndexPrice = webSocketService.subscribe('trading', 'indexprice', { type: 'indexprice' }, (data) => {
          lastMessageTime.current = Date.now();
          if (process.env.NODE_ENV === 'development') {
            console.log(`🔍 Raw indexprice data received (Iran): ${Array.isArray(data) ? data.length + ' items' : 'single item'}`);
          }

          // Parse Iran server JSON response
          let parsedData = data;
          if (typeof data === 'string') {
            try {
              parsedData = JSON.parse(data);
            } catch (error) {
              console.error('❌ Error parsing Iran indexprice data:', error);
              return;
            }
          }

          // Handle Iran server USDT price format
          if (Array.isArray(parsedData) && parsedData[0] === "usdt-price" && parsedData[1]) {
            const usdtPriceData = parsedData[1];
            if (process.env.NODE_ENV === 'development') {
              console.log('💰 USDT price update (Iran):', usdtPriceData.USDTIRT);
            }
            dispatch({ type: "SET_USDT_PRICE", payload: usdtPriceData });
            sessionStorage.setItem("usdtPrice", JSON.stringify(usdtPriceData));
          } else if (parsedData && parsedData.USDTIRT) {
            // Direct USDT price format
            if (process.env.NODE_ENV === 'development') {
              console.log('💰 USDT price update direct (Iran):', parsedData.USDTIRT);
            }
            dispatch({ type: "SET_USDT_PRICE", payload: parsedData });
            sessionStorage.setItem("usdtPrice", JSON.stringify(parsedData));
          }
        });

        unsubscribeUsdtPrice = webSocketService.subscribe('trading', 'usdt-price', { type: 'usdt-price' }, (data) => {
          lastMessageTime.current = Date.now();
          console.log('🔍 Raw usdt-price data received (Iran):', data);

          // Parse Iran server JSON response
          let parsedData = data;
          if (typeof data === 'string') {
            try {
              parsedData = JSON.parse(data);
            } catch (error) {
              console.error('❌ Error parsing Iran usdt-price data:', error);
              return;
            }
          }

          // Handle Iran server direct USDT price updates
          if (parsedData && parsedData.USDTIRT) {
            if (process.env.NODE_ENV === 'development') {
              console.log('💰 USDT price update via usdt-price (Iran):', parsedData.USDTIRT);
            }
            dispatch({ type: "SET_USDT_PRICE", payload: parsedData });
            sessionStorage.setItem("usdtPrice", JSON.stringify(parsedData));
          }
        });
      } else {
        // Germany server: Use existing logic
        unsubscribeIndexPrice = webSocketService.subscribe('trading', 'indexprice', {}, (data) => {
          lastMessageTime.current = Date.now();
          const serverName = webSocketService.connectionStates.get('trading')?.currentServer || 'Unknown';
          if (process.env.NODE_ENV === 'development') {
            console.log(`🔍 Raw indexprice data received (${serverName}): ${Array.isArray(data) ? data.length + ' items' : 'single item'}`);
          }

          // Handle server USDT price updates
          if (Array.isArray(data) && data[0] === "usdt-price" && data[1]) {
            const usdtPriceData = data[1];
            if (process.env.NODE_ENV === 'development') {
              console.log(`💰 USDT price update (${serverName}):`, usdtPriceData.USDTIRT);
            }
            dispatch({ type: "SET_USDT_PRICE", payload: usdtPriceData });
            sessionStorage.setItem("usdtPrice", JSON.stringify(usdtPriceData));
          }
        });

        unsubscribeUsdtPrice = webSocketService.subscribe('trading', 'usdt-price', {}, (data) => {
          lastMessageTime.current = Date.now();
          const serverName = webSocketService.connectionStates.get('trading')?.currentServer || 'Unknown';
          if (process.env.NODE_ENV === 'development') {
            console.log(`🔍 Raw usdt-price data received (${serverName}): ${data?.USDTIRT || 'no USDTIRT'}`);
          }

          if (data && data.USDTIRT) {
            if (process.env.NODE_ENV === 'development') {
              console.log(`💰 USDT price update via usdt-price (${serverName}):`, data.USDTIRT);
            }
            dispatch({ type: "SET_USDT_PRICE", payload: data });
            sessionStorage.setItem("usdtPrice", JSON.stringify(data));
          }
        });
      }

      // Subscribe to additional server events for complete data
      let unsubscribeDeals, unsubscribeOrderbook;

      if (isIranServer) {
        // Iran server subscriptions
        // Note: Deals are handled by trading components, no need to subscribe here

        // Subscribe to orderbook for trading data (requires market_pair for Iran server)
        // For general orderbook subscription, use TRXUSDT as default
        unsubscribeOrderbook = webSocketService.subscribe('trading', 'orderbook', {
          type: 'orderbook',
          market_pair: 'TRXUSDT'
        }, (data) => {
          console.log('📖 Orderbook data received (Iran):', data);
          // Orderbook data is handled by trading components, just log for now
        });
      } else {
        // Germany server subscriptions
        unsubscribeDeals = webSocketService.subscribe('trading', 'deals', { symbol: 'TRXUSDT' }, (data) => {
          console.log('💱 Deals data received (Germany):', data);
          // Deals data is handled by trading components, just log for now
        });

        // Subscribe to orderbook for trading data (Germany server uses ask-bids event)
        unsubscribeOrderbook = webSocketService.subscribe('trading', 'ask-bids', { symbol: 'TRXUSDT' }, (data) => {
          console.log('📖 Orderbook data received (Germany):', data);
          // Orderbook data is handled by trading components, just log for now
        });
      }

      // Note: WebSocketService.subscribe() already handles emission
      // No need to manually emit here as the service handles it properly
      console.log(`✅ All subscriptions set up for ${isIranServer ? 'Iran' : 'Germany'} server`);

      // Store cleanup functions for later use
      window.priceSubscriptionCleanup = () => {
        if (typeof unsubscribePrices === 'function') unsubscribePrices();
        if (typeof unsubscribeIndexPrice === 'function') unsubscribeIndexPrice();
        if (typeof unsubscribeUsdtPrice === 'function') unsubscribeUsdtPrice();
        // Note: Deals cleanup handled by trading components
        if (typeof unsubscribeOrderbook === 'function') unsubscribeOrderbook();
      };
    }

    // Cleanup function for the useEffect
    return () => {
      if (window.priceSubscriptionCleanup) {
        window.priceSubscriptionCleanup();
        delete window.priceSubscriptionCleanup;
      }
    };
  }, [dispatch, isTradingConnected]);

  // Filter initial cached data when filter data becomes available
  useEffect(() => {
    if (filterCoins && filterCoins.length > 0) {
      console.log('🔄 Filtering initial cached data');
      // Get the original unfiltered data from localStorage if available
      const originalData = JSON.parse(localStorage.getItem("coins_original") || "[]");
      if (originalData.length > 0) {
        const filteredData = filterSupportedMarkets(originalData);
        // Add source information to cached data based on stored source
        const storedSource = localStorage.getItem("price_source") || "unknown";
        const dataWithSource = filteredData.map(item => ({
          ...item,
          priceSource: item.priceSource || storedSource
        }));
        dispatch({ type: "SET_FILTERED_PRICES", payload: dataWithSource });
        console.log('💾 Setting localStorage coins (initial filter):', dataWithSource.length);
        localStorage.setItem("coins", JSON.stringify(dataWithSource));
      }
    }
  }, [filterCoins, filterSupportedMarkets]);

  // Re-filter existing prices when filter data changes
  useEffect(() => {
    if (filterCoins && filterCoins.length > 0 && !filterLoading) {
      console.log('🔄 Re-filtering existing prices due to filter data change');
      // Get the original unfiltered data from localStorage if available
      const originalData = JSON.parse(localStorage.getItem("coins_original") || "[]");

      console.log('🔍 Re-filtering debug:', {
        filterCoinsLength: filterCoins.length,
        originalDataLength: originalData.length,
        currentPricesLength: state.prices.length,
        filterLoading
      });

      if (originalData.length > 0) {
        const filteredData = filterSupportedMarkets(originalData);
        console.log('🔄 Re-filtering result:', {
          input: originalData.length,
          output: filteredData.length
        });

        // Only update if we have filtered data or if the original data was empty
        // This prevents temporarily showing empty state during filtering
        if (filteredData.length > 0 || originalData.length === 0) {
          dispatch({ type: "SET_FILTERED_PRICES", payload: filteredData });
          console.log('💾 Setting localStorage coins (re-filter):', filteredData.length);
          localStorage.setItem("coins", JSON.stringify(filteredData));
        } else {
          console.log('⚠️ Re-filtering returned empty array, keeping current state to avoid blank UI');
        }
      } else if (state.prices.length > 0) {
        // Fallback: use current prices if no original data available
        const filteredData = filterSupportedMarkets(state.prices);
        console.log('🔄 Re-filtering fallback result:', {
          input: state.prices.length,
          output: filteredData.length
        });

        // Only update if we have filtered data or if the current prices were empty
        if (filteredData.length > 0 || state.prices.length === 0) {
          dispatch({ type: "SET_FILTERED_PRICES", payload: filteredData });
          console.log('💾 Setting localStorage coins (re-filter fallback):', filteredData.length);
          localStorage.setItem("coins", JSON.stringify(filteredData));
        } else {
          console.log('⚠️ Re-filtering fallback returned empty array, keeping current state to avoid blank UI');
        }
      }
    } else if (!filterLoading && (!filterCoins || filterCoins.length === 0)) {
      // If filter data is empty but not loading, try to restore from cache
      const cachedPrices = JSON.parse(localStorage.getItem("coins") || "[]");
      if (cachedPrices.length > 0 && state.prices.length === 0) {
        console.log('🔄 Restoring cached prices while filter data is unavailable:', cachedPrices.length);
        dispatch({ type: "SET_FILTERED_PRICES", payload: cachedPrices });
      }
    }
  }, [filterCoins, filterLoading, filterSupportedMarkets]);

  // Data timeout checker - only warn if connection is truly problematic
  useEffect(() => {
    const checkDataTimer = setInterval(() => {
      const timeSinceLastMessage = Date.now() - lastMessageTime.current;
      // Only show warning if connection is truly stale (>2 minutes) to reduce noise
      if (timeSinceLastMessage > 120000) {
        console.log(`⚠️ No data received for ${Math.round(timeSinceLastMessage / 1000)}s, connection might be stale`);
      }
    }, RECONNECT_INTERVAL);

    return () => clearInterval(checkDataTimer);
  }, []);

  // اگر بعد از فلان ثانیه دیتا از سوکت نیامد، از ای پی ای استفاده می‌کنیم
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!state.usdtPrice) {
        console.log("⚠️ Unable to receive USDT price from WebSocket. Switching to API");
        setTimeoutReached(true);
      }
    }, USDT_PRICE_API_TIMEOUT);

    if (timeoutReached && usdtPriceFromAPI) {
      console.log('🔄 Using API fallback for USDT price:', usdtPriceFromAPI);
      dispatch({ type: "SET_USDT_PRICE", payload: usdtPriceFromAPI });
      sessionStorage.setItem("usdtPrice", JSON.stringify(usdtPriceFromAPI));
    }

    return () => clearTimeout(timeout);
  }, [timeoutReached, state.usdtPrice, usdtPriceFromAPI]);

  // اگر قیمت از سوکت رسید، تایمر را ریست می‌کنیم
  useEffect(() => {
    if (state.usdtPrice) {
      console.log("✅ USDT price state updated:", state.usdtPrice, "- Resetting API timeout timer");
      setTimeoutReached(false);
    }
  }, [state.usdtPrice]);

  return (
    <CoinPriceContext.Provider
      value={{
        state,
        filterLoading, // Add filter loading state to context
        // Legacy compatibility - these are no longer used but kept for backward compatibility
        socket: socket.current,
        connectionState: connectionState.current,
        emit: () => console.warn('emit() is deprecated, use webSocketService directly'),
        resetOrderBookTimeouts: () => console.warn('resetOrderBookTimeouts() is deprecated'),
        resetDealsTimeouts: () => console.warn('resetDealsTimeouts() is deprecated'),
      }}
    >
      {children}
    </CoinPriceContext.Provider>
  );
};

export const useCoinPrice = (activeSymbol) => {
  const { state, filterLoading, socket, connectionState, emit, resetOrderBookTimeouts, resetDealsTimeouts } =
    useContext(CoinPriceContext);

  // Memoize filtered prices to prevent unnecessary re-renders
  const filteredPrices = useMemo(() => {
    const result = activeSymbol
      ? state.prices.filter((item) => item.symbol === activeSymbol)
      : state.prices;

    // Add logging to see what's being filtered
    if (activeSymbol) {
      console.log('🔍 useCoinPrice filtering for activeSymbol:', {
        activeSymbol,
        totalPrices: state.prices?.length || 0,
        filteredCount: result?.length || 0,
        foundMatch: result?.length > 0
      });
    }

    return result;
  }, [state.prices, activeSymbol]);

  // Debug logging only in development and when needed
  if (process.env.NODE_ENV === 'development' && Math.random() < 0.01) { // Only log 1% of calls
    console.log('🔍 useCoinPrice return:', {
      filteredPricesLength: filteredPrices?.length || 0,
      statePricesLength: state.prices?.length || 0,
      activeSymbol,
      isFilterLoading: filterLoading,
      usdtPrice: !!state.usdtPrice
    });
  }

  // Return object without memoization to allow immediate price updates
  return {
    prices: filteredPrices,
    usdtPrice: state.usdtPrice,
    isFilterLoading: Boolean(filterLoading), // Ensure always boolean
    // Legacy compatibility - these are no longer used but kept for backward compatibility
    socket: socket?.current || null,
    connectionState: connectionState?.current || { connected: false },
    emit: () => console.warn('emit() is deprecated, use webSocketService directly'),
    resetOrderBookTimeouts: () => console.warn('resetOrderBookTimeouts() is deprecated'),
    resetDealsTimeouts: () => console.warn('resetDealsTimeouts() is deprecated'),
  };
};
